import { Component } from "@theme/component";
import { DialogCloseEvent, DialogComponent } from "@theme/dialog";
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from "@theme/events";
import { getIOSVersion } from "@theme/utilities";

const DAC_RECHARGE_MOUNT = "__dacMount";
const SHOPIFY_BUYER_CONSENT_ID = "shopify-buyer-consent";
const SHOPIFY_SUBSCRIPTION_POLICY_ID = "shopify-subscription-policy-button";
const DAC_PARKED_BUYER_CONSENT_ID = "dac-pdp-buyer-consent";
const DAC_PARKED_SUBSCRIPTION_POLICY_ID = "dac-pdp-subscription-policy-button";
const DAC_PARKED_CONSENT_ATTR = "data-dac-parked-buyer-consent";

/** @type {ReturnType<typeof setTimeout> | null} */
let rechargePurchaseTimeout = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let rechargeTopPriceTimeout = null;
let rechargeMounting = false;

/** @type {{ subscriptionActive: boolean, sellingPlanId: string } | null} */
let pdpRechargePurchaseState = null;

/**
 * @returns {{ createWidget?: Function, destroyAllWidgets?: Function, getWidgets?: Function } | undefined}
 */
function getRechargeApi() {
  return /** @type {{ createWidget?: Function, destroyAllWidgets?: Function, getWidgets?: Function } | undefined} */ (
    window
  ).ReChargeWidget;
}

function getRechargeWidgets() {
  return getRechargeApi()?.getWidgets?.() || [];
}

function hasPdpRechargeWidget() {
  return Boolean(document.querySelector(".product-information .rc-widget, .product-information .rc-container-wrapper"));
}

/**
 * Recharge's legacy widget exposes its mounted DOM node as `widget.container`.
 * Keep the other keys as version-safe fallbacks.
 * @param {object} widget
 * @returns {Node | null}
 */
function getRechargeWidgetElement(widget) {
  const el = widget?.container || widget?.injectionParent || widget?.$el || widget?.el;
  return el instanceof Node ? el : null;
}

/**
 * DAC customization: payment_button always emits id="shopify-buyer-consent".
 * PDP + drawer means duplicate ids, so Shopify unhides the PDP copy. Park non-drawer
 * copies while the drawer is open so Subscribe shows consent in the drawer.
 */
function preferDrawerBuyerConsent() {
  const drawer = document.getElementById("quick-add-drawer-content");
  if (!(drawer instanceof HTMLElement)) return;
  if (!(drawer.querySelector(`[id="${SHOPIFY_BUYER_CONSENT_ID}"]`) instanceof HTMLElement)) return;

  document.querySelectorAll(`[id="${SHOPIFY_BUYER_CONSENT_ID}"]`).forEach((el) => {
    if (!(el instanceof HTMLElement) || drawer.contains(el)) return;
    el.id = DAC_PARKED_BUYER_CONSENT_ID;
    el.setAttribute(DAC_PARKED_CONSENT_ATTR, "");
    el.hidden = true;
  });

  document.querySelectorAll(`[id="${SHOPIFY_SUBSCRIPTION_POLICY_ID}"]`).forEach((el) => {
    if (!(el instanceof HTMLElement) || drawer.contains(el)) return;
    el.id = DAC_PARKED_SUBSCRIPTION_POLICY_ID;
  });
}

function restorePdpBuyerConsent() {
  document.querySelectorAll(`[${DAC_PARKED_CONSENT_ATTR}]`).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.id = SHOPIFY_BUYER_CONSENT_ID;
    el.removeAttribute(DAC_PARKED_CONSENT_ATTR);
    el.hidden = false;
  });

  document.querySelectorAll(`[id="${DAC_PARKED_SUBSCRIPTION_POLICY_ID}"]`).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.id = SHOPIFY_SUBSCRIPTION_POLICY_ID;
  });
}

/**
 * @param {HTMLFormElement | Element | null | undefined} form
 * @param {string} sellingPlanId
 */
function applySellingPlanToForm(form, sellingPlanId) {
  if (!(form instanceof HTMLFormElement)) return;

  let input = form.querySelector('input[name="selling_plan"]');
  if (!sellingPlanId) {
    if (input instanceof HTMLInputElement) input.remove();
    return;
  }

  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement("input");
    input.type = "hidden";
    input.name = "selling_plan";
    form.appendChild(input);
  }

  input.value = sellingPlanId;
}

function snapshotPdpRechargePurchaseState() {
  const pdpRoot = document.querySelector(".product-information");
  const form = pdpRoot?.querySelector(".buy-buttons-block--has-selling-plans form[data-type='add-to-cart-form']");
  if (!(pdpRoot instanceof Element) || !(form instanceof HTMLFormElement) || !form.querySelector(".rc-widget, .rc-container-wrapper")) {
    pdpRechargePurchaseState = null;
    return;
  }

  const sellingPlanId = form.querySelector('input[name="selling_plan"]')?.value || "";
  let subscriptionActive = Boolean(sellingPlanId);
  const pdpWidget =
    getRechargeWidgets().find((widget) => widget[DAC_RECHARGE_MOUNT] !== "drawer") ||
    getRechargeWidgets().find((widget) => {
      const el = getRechargeWidgetElement(widget);
      return el instanceof Node && pdpRoot.contains(el);
    });

  subscriptionActive = getPdpSubscriptionActive(pdpRoot, pdpWidget);

  pdpRechargePurchaseState = { subscriptionActive, sellingPlanId };
}

/**
 * @param {Element | null | undefined} pdpRoot
 * @param {object | undefined} widget
 * @returns {boolean}
 */
function getPdpSubscriptionActive(pdpRoot, widget) {
  const form = pdpRoot?.querySelector(".buy-buttons-block--has-selling-plans form[data-type='add-to-cart-form']");
  const sellingPlanId = form?.querySelector('input[name="selling_plan"]')?.value || "";
  let subscriptionActive = Boolean(sellingPlanId);

  if (typeof widget?.store?.state?.isSubscriptionActive === "boolean") {
    subscriptionActive = widget.store.state.isSubscriptionActive;
  } else {
    const activeOption = pdpRoot?.querySelector(".rc-radio.rc_widget__option--active, .rc-option--active");
    if (activeOption) subscriptionActive = /sub/i.test(`${activeOption.className} ${activeOption.textContent || ""}`);
  }

  return subscriptionActive;
}

function applyPdpSellingPlanFallback() {
  const state = pdpRechargePurchaseState;
  if (!state) return;

  const form = document.querySelector(
    ".product-information .buy-buttons-block--has-selling-plans form[data-type='add-to-cart-form']"
  );
  if (!(form instanceof HTMLFormElement) || form.querySelector(".rc-widget, .rc-container-wrapper")) return;

  applySellingPlanToForm(form, state.subscriptionActive ? state.sellingPlanId : "");
}

function restorePdpRechargePurchaseState() {
  const state = pdpRechargePurchaseState;
  if (!state) return;

  const pdpRoot = document.querySelector(".product-information");
  const form = pdpRoot?.querySelector(".buy-buttons-block--has-selling-plans form[data-type='add-to-cart-form']");
  if (!(pdpRoot instanceof Element)) {
    pdpRechargePurchaseState = null;
    return;
  }

  const radios = [...pdpRoot.querySelectorAll('.rc-radio__input, .rc-option input[type="radio"]')];
  const radioLabel = (el) =>
    `${el.className} ${el.id} ${el.closest("label")?.textContent || ""} ${el.closest(".rc-radio, .rc-option")?.textContent || ""}`;
  const target = state.subscriptionActive
    ? radios.find((el) => /sub/i.test(radioLabel(el)))
    : radios.find((el) => /one/i.test(radioLabel(el)));

  if (target instanceof HTMLInputElement && !target.checked) target.click();

  const planSelect = pdpRoot.querySelector(".rc_widget__option__plans__dropdown");
  if (state.sellingPlanId && planSelect instanceof HTMLSelectElement && planSelect.value !== state.sellingPlanId) {
    planSelect.value = state.sellingPlanId;
    planSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  window.setTimeout(() => {
    applySellingPlanToForm(form, state.subscriptionActive ? state.sellingPlanId : "");
  }, 50);
  pdpRechargePurchaseState = null;
}

/**
 * @param {object} widget
 */
function destroySingleRechargeWidget(widget) {
  try {
    if (typeof widget.destroy === "function") widget.destroy();
    else if (typeof widget.unmount === "function") widget.unmount();
    else if (typeof widget.$destroy === "function") widget.$destroy();
  } catch {
    // Recharge widget teardown APIs differ by version
  }
}

/**
 * DAC customization: never call destroyAllWidgets while a PDP widget is still present.
 */
function destroyDrawerRechargeWidgets() {
  const api = getRechargeApi();
  const widgets = getRechargeWidgets();
  const drawerWidgets = widgets.filter((widget) => widget[DAC_RECHARGE_MOUNT] === "drawer");
  const otherWidgets = widgets.filter((widget) => widget[DAC_RECHARGE_MOUNT] !== "drawer");
  const content = document.getElementById("quick-add-drawer-content");

  if (hasPdpRechargeWidget() || otherWidgets.length) {
    drawerWidgets.forEach(destroySingleRechargeWidget);
    content?.querySelectorAll(".rc-widget, .rc-container-wrapper").forEach((el) => el.remove());
    const injection = content?.querySelector(".rc-widget-injection-parent");
    if (injection instanceof HTMLElement) {
      injection.replaceChildren();
      delete injection.dataset.dacRechargeBound;
    }
    return;
  }

  api?.destroyAllWidgets?.();
}

/**
 * @param {ParentNode | null} [priceRoot]
 * @returns {object[]}
 */
function getWidgetsForSync(priceRoot) {
  const widgets = getRechargeWidgets();
  // Scope by the widget's live container element. destroyAllWidgets is guarded, so the
  // Recharge registry can retain stale drawer widgets whose containers are detached; the
  // isConnected check keeps those (and cross-context widgets) out of the sync.
  const scoped = (scopeSelector) =>
    widgets.filter((widget) => {
      const el = getRechargeWidgetElement(widget);
      return el instanceof Element && el.isConnected && el.closest(scopeSelector);
    });

  if (priceRoot instanceof Element && (priceRoot.id === "quick-add-drawer-content" || priceRoot.closest("#quick-add-drawer-content"))) {
    const drawer = scoped("#quick-add-drawer-content");
    if (drawer.length) return drawer;
  }

  if (priceRoot instanceof Element && (priceRoot.matches(".product-information") || priceRoot.closest(".product-information"))) {
    const pdp = scoped(".product-information");
    if (pdp.length) return pdp;
  }

  if (priceRoot instanceof Element) {
    const inRoot = widgets.filter((widget) => {
      const el = getRechargeWidgetElement(widget);
      return el instanceof Node && priceRoot.contains(el);
    });
    if (inRoot.length) return inRoot;
  }

  return widgets.length === 1 ? widgets : [];
}

/**
 * DAC customization: Recharge only auto-injects on page load; remount into AJAX drawer forms.
 * @param {ParentNode} root
 * @param {{ remount?: boolean }} [options]
 * @returns {Promise<void>}
 */
async function mountRechargeInRoot(root, options = {}) {
  const { remount = false } = options;
  const buyButtons = root.querySelector(".buy-buttons-block--has-selling-plans");
  const productForm = buyButtons?.querySelector("product-form-component");
  const productId = Number(productForm?.getAttribute("data-product-id"));
  const form = buyButtons?.querySelector('form[data-type="add-to-cart-form"]');
  if (!buyButtons || !Number.isFinite(productId) || !(form instanceof HTMLFormElement)) return;

  const api = getRechargeApi();
  if (typeof api?.createWidget !== "function") return;

  const existingWidget = form.querySelector(".rc-widget, .rc-container-wrapper");
  if (existingWidget && !remount) return;
  if (rechargeMounting) return;

  const isDrawer =
    root instanceof Element &&
    (root.id === "quick-add-drawer-content" || Boolean(root.closest("#quick-add-drawer-content")));
  const scopeSelector = isDrawer ? "#quick-add-drawer-content" : ".product-information";

  rechargeMounting = true;
  buyButtons.classList.toggle("dac-recharge-remounting", remount || !isDrawer);

  if (isDrawer) snapshotPdpRechargePurchaseState();

  if (isDrawer) destroyDrawerRechargeWidgets();
  else api.destroyAllWidgets?.();

  let injectionParent = form.querySelector(".rc-widget-injection-parent");
  if (!(injectionParent instanceof HTMLElement)) {
    injectionParent = document.createElement("div");
    injectionParent.className = "rc-widget-injection-parent";
    const skeleton = form.querySelector(".dac-recharge-skeleton");
    const buttons = form.querySelector(".product-form-buttons");
    if (skeleton) skeleton.insertAdjacentElement("afterend", injectionParent);
    else if (buttons) form.insertBefore(injectionParent, buttons);
    else form.appendChild(injectionParent);
  } else {
    injectionParent.replaceChildren();
  }

  // form.id is the hidden [name="id"] input (named property), not the form's id attribute
  const formId = form.getAttribute("id");
  const variantSelectors = [`${scopeSelector} .variant-option--fieldset input[type="radio"]`];
  if (formId) variantSelectors.unshift(`#${CSS.escape(formId)} [name="id"]`);

  try {
    const beforeWidgets = getRechargeWidgets();
    await api.createWidget({
      productId,
      injectionParent,
      selectors: {
        variant: variantSelectors,
        price: [
          `${scopeSelector} .product-price-block product-price .recharge-inner-most-price`,
          `${scopeSelector} .product-price-block product-price .price`,
        ],
      },
    });

    const afterWidgets = getRechargeWidgets();
    const created =
      afterWidgets.find((widget) => widget.injectionParent === injectionParent) ||
      afterWidgets.find((widget) => !beforeWidgets.includes(widget)) ||
      afterWidgets.find((widget) => {
        const el = getRechargeWidgetElement(widget);
        return el instanceof Node && injectionParent.contains(el);
      });
    if (created) created[DAC_RECHARGE_MOUNT] = isDrawer ? "drawer" : "pdp";

    if (isDrawer) applyPdpSellingPlanFallback();

    // Recharge can leave the wrapped theme price stale when toggling Subscribe / One-time
    if (!injectionParent.dataset.dacRechargeBound) {
      injectionParent.dataset.dacRechargeBound = "true";
      injectionParent.addEventListener("change", () => {
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (!variantId) return;
        if (rechargePurchaseTimeout) clearTimeout(rechargePurchaseTimeout);
        rechargePurchaseTimeout = window.setTimeout(() => {
          rechargePurchaseTimeout = null;
          syncRechargeVariant(variantId, root);
        }, 50);
      });
    }

    const currentVariantId = form.querySelector('input[name="id"]')?.value;
    if (currentVariantId) await syncRechargeVariant(currentVariantId, root);
  } catch (error) {
    console.warn("[DAC] Failed to mount Recharge widget", error);
  } finally {
    buyButtons.classList.remove("dac-recharge-remounting");
    rechargeMounting = false;
  }
}

/**
 * DAC customization: copy the PDP main price block into sticky ATC (subscription or one-time).
 */
function mirrorPdpPriceToSticky() {
  const mainPrice = document.querySelector(".product-information .product-price-block");
  const stickyPrice = document.querySelector("sticky-add-to-cart product-price");
  if (!mainPrice || !stickyPrice) return;

  const mainContainer = mainPrice.querySelector('[ref="priceContainer"]');
  const stickyContainer = stickyPrice.querySelector('[ref="priceContainer"]');
  if (!mainContainer || !stickyContainer) return;

  stickyContainer.classList.toggle("price--on-sale", mainContainer.classList.contains("price--on-sale"));

  const mainRegular = mainContainer.querySelector(".price__regular");
  const stickyRegular = stickyContainer.querySelector(".price__regular");
  if (mainRegular && stickyRegular && stickyRegular.innerHTML !== mainRegular.innerHTML) {
    stickyRegular.innerHTML = mainRegular.innerHTML;
  }

  const mainSale = mainContainer.querySelector(".price__sale");
  const stickySale = stickyContainer.querySelector(".price__sale");
  if (mainSale && stickySale && stickySale.innerHTML !== mainSale.innerHTML) {
    stickySale.innerHTML = mainSale.innerHTML;
  }

  if (mainPrice.style.display) {
    stickyPrice.style.display = mainPrice.style.display;
  } else {
    stickyPrice.style.removeProperty("display");
  }
}

/**
 * @param {string} topPrice
 * @param {boolean} hasDiscount
 */
function applyStickyRechargePrice(topPrice, hasDiscount) {
  const stickyContainer = document.querySelector('sticky-add-to-cart product-price [ref="priceContainer"]');
  if (!stickyContainer) return;

  stickyContainer.classList.toggle("price--on-sale", hasDiscount);

  const regularEl = stickyContainer.querySelector(".price__regular .price");
  const saleEl = stickyContainer.querySelector(".price__sale .price, .price__sale .price--sale");

  if (hasDiscount && saleEl) {
    saleEl.textContent = topPrice;
  } else if (regularEl) {
    regularEl.textContent = topPrice;
  }

  const stickyPrice = document.querySelector("sticky-add-to-cart product-price");
  stickyPrice?.style.removeProperty("display");
}

/**
 * @param {Element} pdpRoot
 */
function bindPdpRechargeStickySync(pdpRoot) {
  if (pdpRoot.dataset.dacRechargeStickyBound) return;
  pdpRoot.dataset.dacRechargeStickyBound = "true";

  const sync = () => {
    const variantId = pdpRoot.querySelector('input[name="id"]')?.value;
    if (!variantId) return;
    if (rechargePurchaseTimeout) clearTimeout(rechargePurchaseTimeout);
    rechargePurchaseTimeout = window.setTimeout(() => {
      rechargePurchaseTimeout = null;
      syncRechargeVariant(variantId, pdpRoot);
    }, 50);
  };

  pdpRoot.addEventListener("change", (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(".rc-widget, .rc-container-wrapper, .rc-radio, .rc-option, .rc-selling-plans")) return;
    sync();
  });

  pdpRoot.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;
    if (
      !event.target.closest(
        '.rc-radio__input, .rc-option input[type="radio"], .rc_widget__option__selector, .rc-radio__label, .rc-option__label'
      )
    ) {
      return;
    }
    sync();
  });
}

/**
 * DAC customization: push the selected variant into Recharge so widget prices update.
 * @param {string | number} variantId
 * @param {ParentNode | null} [priceRoot]
 * @returns {Promise<void>}
 */
async function syncRechargeVariant(variantId, priceRoot) {
  const widgets = getWidgetsForSync(priceRoot);
  const id = String(variantId);
  const priceScope =
    priceRoot instanceof Element || priceRoot instanceof DocumentFragment
      ? priceRoot
      : document.getElementById("quick-add-drawer-content");

  if (rechargeTopPriceTimeout) {
    clearTimeout(rechargeTopPriceTimeout);
    rechargeTopPriceTimeout = null;
  }

  if (!widgets.length) {
    mirrorPdpPriceToSticky();
    return;
  }

  for (const widget of widgets) {
    try {
      await widget.store?.dispatch?.("setSelectedVariantId", id);
    } catch {
      // Recharge store API can differ by widget version
    }

    const variant = widget.variants?.variants?.find((item) => String(item.external_variant_id) === id);
    const price = variant?.prices?.[0];
    if (!price || typeof widget.updateOnetimeSubscriptionRadioPrices !== "function") continue;

    const priceOnetime = `$${price.unit_price}`;
    const discounted =
      price.plans?.find((plan) => Number(plan.discount_value) > 0)?.discounted_price || price.unit_price;
    const priceSubsave = `$${discounted}`;
    const pdpRoot =
      priceRoot instanceof Element && priceRoot.closest(".product-information")
        ? priceRoot.closest(".product-information")
        : document.querySelector(".product-information");
    const subscriptionActive = getPdpSubscriptionActive(pdpRoot, widget);
    const topPrice = subscriptionActive ? priceSubsave : priceOnetime;
    const hasDiscount = priceOnetime !== priceSubsave;

    try {
      widget.updateOnetimeSubscriptionRadioPrices({
        hasDiscount,
        priceOnetime,
        priceSubsave,
      });
    } catch {
      // Ignore widget internal price-template errors
    }

    const applyTopPrice = () => {
      const active = getPdpSubscriptionActive(pdpRoot, widget);
      const resolvedTopPrice = active ? priceSubsave : priceOnetime;

      try {
        widget.onProductPriceChange?.({
          priceOnetime,
          priceSubsave,
          subscriptionActive: active,
        });
      } catch {
        // Ignore widget internal price-template errors
      }

      priceScope?.querySelectorAll(".product-price-block .recharge-inner-most-price").forEach((el) => {
        el.textContent = resolvedTopPrice;
      });

      applyStickyRechargePrice(resolvedTopPrice, hasDiscount && active);
      mirrorPdpPriceToSticky();
    };

    applyTopPrice();
    // Recharge's mutation watcher can revert the top price after variant change; re-apply once it settles
    rechargeTopPriceTimeout = window.setTimeout(applyTopPrice, 50);
    window.setTimeout(applyTopPrice, 150);
  }
}

/**
 * DAC customization: keep PDP sticky ATC aligned with Recharge on variant / purchase-option changes.
 * @param {Event & { detail?: { resource?: { id?: string | number } } }} event
 */
function handlePdpRechargeVariantUpdate(event) {
  const variantId = event.detail?.resource?.id;
  if (!variantId) return;
  if (event.target instanceof Element && event.target.closest("product-card, quick-add-dialog, #quick-add-drawer-content")) {
    return;
  }
  if (!hasPdpRechargeWidget()) return;

  const pdpRoot = document.querySelector(".product-information");
  if (!pdpRoot) return;

  syncRechargeVariant(variantId, pdpRoot);
}

document.addEventListener(ThemeEvents.variantUpdate, handlePdpRechargeVariantUpdate);

function scheduleInitialPdpStickyPriceSync() {
  const run = () => {
    if (!document.querySelector("sticky-add-to-cart") || !hasPdpRechargeWidget()) return;

    const pdpRoot = document.querySelector(".product-information");
    if (pdpRoot instanceof Element) bindPdpRechargeStickySync(pdpRoot);

    mirrorPdpPriceToSticky();

    const variantId = pdpRoot?.querySelector('input[name="id"]')?.value;
    if (variantId && pdpRoot) {
      syncRechargeVariant(variantId, pdpRoot);
    }
  };

  run();
  window.setTimeout(run, 100);
  window.setTimeout(run, 500);
  window.setTimeout(run, 1500);
}

function schedulePdpRechargeStickySync() {
  const pdpRoot = document.querySelector(".product-information");
  if (!(pdpRoot instanceof Element)) return;

  if (hasPdpRechargeWidget()) {
    bindPdpRechargeStickySync(pdpRoot);
    scheduleInitialPdpStickyPriceSync();
    return;
  }

  let attempts = 0;
  const poll = window.setInterval(() => {
    attempts += 1;
    if (!hasPdpRechargeWidget()) {
      if (attempts >= 24) window.clearInterval(poll);
      return;
    }

    window.clearInterval(poll);
    bindPdpRechargeStickySync(pdpRoot);
    scheduleInitialPdpStickyPriceSync();
  }, 250);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", schedulePdpRechargeStickySync, { once: true });
} else {
  schedulePdpRechargeStickySync();
}

/**
 * DAC customization: tear down drawer Recharge without wiping PDP; remount PDP only if needed.
 * @returns {Promise<void>}
 */
async function restorePdpRechargeAfterDrawerClose() {
  destroyDrawerRechargeWidgets();

  const pdpRoot = document.querySelector(".product-information");
  if (!pdpRoot) {
    pdpRechargePurchaseState = null;
    return;
  }

  const form = pdpRoot.querySelector(".buy-buttons-block--has-selling-plans form[data-type='add-to-cart-form']");
  if (!(form instanceof HTMLFormElement)) {
    pdpRechargePurchaseState = null;
    return;
  }

  if (!form.querySelector(".rc-widget, .rc-container-wrapper")) {
    await mountRechargeInRoot(pdpRoot);
    restorePdpRechargePurchaseState();
    return;
  }

  pdpRechargePurchaseState = null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productUrl() {
    return this.dataset?.productUrl;
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return "";

    const url = new URL(productLink.href);

    if (url.searchParams.has("variant")) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set("variant", selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.quickAddButtonSpinner = this.querySelector(".quick-add__button-spinner");
    this.quickAddButton = this.querySelector(".button-choose-options");
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.#abortController?.abort();
  }

  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.dataset.quickAddMode === "view") return;
    if (event.target.closest("product-card") !== this.closest("product-card")) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    this.setAttribute("data-quick-add-button", quickAddButton);
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute("stay-visible", false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Use the product URL with section_id to get product context
    const currentUrl = `${this.productPageUrl.split("?")[0]}?section_id=${this.quickAddDrawerId}&${this.productPageUrl.split("?")[1]}`;

    let quickAddContent = this.#cachedContent.get(currentUrl);

    if (!quickAddContent) {
      this.addLoading();
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const quickAddContentElement = html.querySelector("[data-quick-add-content]");
        if (quickAddContentElement) {
          quickAddContent = /** @type {Element} */ (quickAddContentElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, quickAddContent);
        }
      }
      this.removeLoading();
    }

    if (quickAddContent) {
      const freshContent = /** @type {Element} */ (quickAddContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      document.dispatchEvent(
        new CustomEvent(ThemeEvents.quickViewLoaded, {
          detail: { productUrl: this.productUrl },
        })
      );

      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(quickAddContent) {
    const quickAddDrawerContent = document.getElementById("quick-add-drawer-content");
    if (!quickAddContent || !quickAddDrawerContent) return;

    quickAddDrawerContent.innerHTML = quickAddContent.innerHTML;
    preferDrawerBuyerConsent();
    this.#syncVariantSelection(quickAddDrawerContent);
    // DAC customization: Recharge does not auto-inject into AJAX-loaded drawer forms
    await mountRechargeInRoot(quickAddDrawerContent);
    preferDrawerBuyerConsent();
    const dialog = document.getElementById("quick-add-dialog");
    const variantInput = quickAddDrawerContent.querySelector('input[name="id"]');
    if (dialog instanceof QuickAddDialog && variantInput instanceof HTMLInputElement) {
      dialog.rememberRechargeVariantId(variantInput.value);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Adds loading class to the add to cart button.
   */
  addLoading() {
    this.quickAddButton?.classList.add("btn--loading");
    this.quickAddButtonSpinner?.classList.remove("hidden");
  }

  /**
   * Removes loading class from the add to cart button.
   */
  removeLoading() {
    this.quickAddButton?.classList.remove("btn--loading");
    this.quickAddButtonSpinner?.classList.add("hidden");
  }

  get quickAddDrawerId() {
    const quickAddDrawer = document.getElementById("quick-add-drawer");
    return quickAddDrawer?.dataset.sectionId;
  }
}
if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {number | null} */
  #currentCartItemCount = null;
  /** @type {string | number | null} */
  #rechargeVariantId = null;

  connectedCallback() {
    super.connectedCallback();

    // Initialize cart count from DOM if available
    this.#initializeCartCount();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    // DAC customization: keep Recharge prices in sync when drawer variant changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleRechargeVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  get productUrl() {
    return this.querySelector(".quick-add__content")?.dataset?.productUrl;
  }

  /**
   * DAC customization: track the variant currently shown in the drawer Recharge widget.
   * @param {string | null | undefined} variantId
   */
  rememberRechargeVariantId(variantId) {
    this.#rechargeVariantId = variantId || null;
  }

  showDialog() {
    super.showDialog();

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.quickViewOpened, {
        detail: { productUrl: this.productUrl },
      })
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Initialize cart count from DOM or cart-count component
   */
  #initializeCartCount() {
    // Try to get count from cart-count component (not in drawer)
    const cartCountElement = document.querySelector('cart-count:not([data-context="drawer"])');
    if (cartCountElement?.refs?.cartBubbleCount) {
      this.#currentCartItemCount = parseInt(cartCountElement.refs.cartBubbleCount.textContent ?? "0", 10);
      return;
    }

    // Fallback: try to get from cart-bubble__text-count class
    const cartBubbleCount = document.querySelector(".cart-bubble__text-count");
    if (cartBubbleCount) {
      this.#currentCartItemCount = parseInt(cartBubbleCount.textContent ?? "0", 10);
      return;
    }

    // If no count found, initialize to 0
    this.#currentCartItemCount = 0;
  }

  /**
   * Closes the dialog only if cart item count actually increased
   * @param {CartAddEvent | CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    // Don't close if there was an error
    if (event.detail.data?.didError) {
      return;
    }

    const itemCount = event.detail.data?.itemCount;
    const isIncremental = event.detail.data?.isIncremental ?? false;

    // If itemCount is missing or invalid, skip processing
    if (itemCount === undefined || itemCount === null) {
      return;
    }

    let newCartItemCount;

    if (isIncremental) {
      // CartAddEvent: itemCount is the delta (quantity added)
      // Handle negative values (shouldn't happen, but defensive)
      const currentCount = this.#currentCartItemCount ?? 0;
      newCartItemCount = Math.max(0, currentCount + itemCount);
    } else {
      // CartUpdateEvent: itemCount is the absolute total count
      // Ensure non-negative
      newCartItemCount = Math.max(0, itemCount);
    }

    const currentCount = this.#currentCartItemCount ?? 0;

    // Only close drawer if cart count actually increased
    // This handles:
    // - Adding items that exceed max quantity (count doesn't increase) -> don't close
    // - Adding items successfully (count increases) -> close drawer
    // - Removing items (count decreases) -> don't close
    if (newCartItemCount > currentCount) {
      this.closeDialog();
    }

    // Always update tracked count to stay in sync
    this.#currentCartItemCount = newCartItemCount;
  };

  /**
   * DAC customization: keep drawer Recharge prices in sync when the variant changes.
   * @param {Event & { detail?: { resource?: { id?: string | number } } }} event
   */
  #handleRechargeVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    const content = document.getElementById("quick-add-drawer-content");
    const hasWidget = content?.querySelector(".rc-widget, .rc-container-wrapper, .rc-widget-injection-parent");

    if (!hasWidget || rechargeMounting) {
      this.#rechargeVariantId = variantId;
      return;
    }

    if (String(variantId) === String(this.#rechargeVariantId)) return;

    this.#rechargeVariantId = variantId;

    const widgets = getWidgetsForSync(content);
    if (widgets.length) {
      syncRechargeVariant(variantId, content);
      return;
    }

    mountRechargeInRoot(content, { remount: true });
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector(".view-product-title a"));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector(".product-header a"));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.#rechargeVariantId = null;
    if (rechargePurchaseTimeout) {
      clearTimeout(rechargePurchaseTimeout);
      rechargePurchaseTimeout = null;
    }
    if (rechargeTopPriceTimeout) {
      clearTimeout(rechargeTopPriceTimeout);
      rechargeTopPriceTimeout = null;
    }
    // DAC customization: return Shopify buyer-consent ids to the PDP, then keep Recharge intact
    restorePdpBuyerConsent();
    restorePdpRechargeAfterDrawerClose();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector("#ResultsList [product-grid-view]");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
